import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  KIND, download, downloadZoneTemplate, errorText, fetchCourierTatHistory, fetchMerchants, fetchTatCouriers,
  fetchTatGrid, fetchZoneSummary, uploadCourierTat,
} from '../ptlSetupApi'
import { ROUTES, fmtDate, num } from '../constants'
import { downloadOdaZoneSample, downloadOdaZones, fetchOdaCouriers, fetchOdaPincodes, uploadOdaZones } from '../../ptl-oda/ptlOdaApi'
import { EDD_TIMING, ODA_EYEBROW, ODA_ROUTES } from '../../ptl-oda/constants'

/** "Last change" KPI from the audit log. */
const lastChange = (latest) => ({
  key: 'last', label: 'Last change', value: latest ? fmtDate(latest.createdAt).split(',')[0] : 'None',
  sub: latest ? latest.createdBy || '' : 'Not uploaded from PTL Merchant Setup yet',
})

const ZONE_FILE = 'CSV with the columns Pincode, City, State, Region, Zone, Green TAX, Special Zone, ODA Type. Zone is a PTL zone id of the standard TAT, or 9999 for not serviceable. ODA Type is ODA 1, ODA 2, ODA 3, Regular or blank.'
const ZONE_CHECKS = ['added', 'changed', 'unchanged', 'removed', 'zoneChanges']

/**
 * What the generic Upload and History screens work on (plan screens U and H). Standard targets are fixed; merchant
 * targets are built for the merchant in the route (:merchantId).
 *
 * now()      KPI cards of the current state, shown on the Upload screen until a file is checked
 * checkKeys  upload check counts shown as KPI cards after the check, in this order
 * effect     what a save applies to, for the confirm dialog warning
 * noun       the target inside a sentence ("Save the standard zone map?")
 * removedTo  where pincodes left out of a Replace file go, for the save button
 */
export const SETUP_TARGETS = {
  'zone-map': () => ({
    kind: KIND.ZONE_MAP,
    merchantId: null,
    label: 'Standard zone map',
    noun: 'standard zone map',
    removedTo: 'to 9999',
    parent: { label: 'Standard Zone Map', to: ROUTES.zoneMap },
    keyLabel: 'Pincode',
    modes: true,
    replaceHint: 'Pincodes missing from the file move to zone 9999 (not serviceable).',
    fileHelp: ZONE_FILE,
    effect: 'It applies to every PTL merchant that uses the standard map, for shipments priced from now on.',
    downloads: [
      { label: 'Download template', get: () => downloadZoneTemplate(), name: 'ptl_zone_map_template.csv' },
      { label: 'Download current map', get: () => download(KIND.ZONE_MAP, null), name: 'ptl_standard_zone_map.csv' },
    ],
    now: async () => {
      const s = await fetchZoneSummary(null)
      return [
        { key: 'mapped', label: 'Pincodes mapped now', value: num(s.pincodesMapped), sub: 'With a serviceable zone' },
        { key: 'ns', label: 'Not serviceable now', value: num(s.notServiceable), tone: s.notServiceable ? 'danger' : 'ok', sub: 'Zone 9999' },
        { key: 'zones', label: 'Zones in use', value: `${num(s.zonesInUse)} / ${num(s.zonesDefined)}`, sub: 'Zones of the standard TAT' },
        lastChange(s.latest),
      ]
    },
    checkKeys: ZONE_CHECKS,
  }),
  'merchant-zone-map': (m) => ({
    kind: KIND.ZONE_MAP,
    merchantId: m.id,
    label: `${m.name} zone map`,
    noun: `${m.name} zone map`,
    removedTo: 'back to the standard zone',
    parent: { label: m.name, to: `${ROUTES.zoneOverrides}/${m.id}` },
    keyLabel: 'Pincode',
    modes: true,
    replaceHint: 'Own pincodes missing from the file go back to the standard zone for this merchant.',
    fileHelp: `${ZONE_FILE} Only ${m.name} uses this file: its pincodes replace the standard zone for this merchant, every other pincode keeps the standard zone.`,
    effect: `It applies only to ${m.name}, for shipments priced from now on.`,
    downloads: [
      { label: 'Download template', get: () => downloadZoneTemplate(), name: 'ptl_zone_map_template.csv' },
      { label: 'Download own pincodes', get: () => download(KIND.ZONE_MAP, m.id), name: `ptl_zone_map_${m.id}.csv` },
    ],
    now: async () => {
      const s = await fetchZoneSummary(m.id)
      return [
        { key: 'own', label: 'Own pincodes now', value: num(s.pincodesMapped + s.notServiceable), sub: 'Replace the standard zone' },
        { key: 'differs', label: 'Zone differs from standard', value: num(s.zoneDiffersFromStandard), sub: 'Of those pincodes' },
        { key: 'ns', label: 'Not serviceable now', value: num(s.notServiceable), tone: s.notServiceable ? 'danger' : undefined, sub: 'Zone 9999 for this merchant' },
        lastChange(s.latest),
      ]
    },
    checkKeys: ZONE_CHECKS,
  }),
  tat: () => ({
    kind: KIND.TAT,
    merchantId: null,
    label: 'Standard TAT',
    noun: 'standard TAT',
    parent: { label: 'Standard TAT', to: ROUTES.tat },
    keyLabel: 'Lane',
    modes: false,
    fileHelp: 'CSV in the Prozo Standard TAT format: Zone, Pickup Zone ID, Pickup Zone, Pickup Area, Drop Zone ID, Drop Zone, Drop Area, Lane, B2B MIN TAT, B2B MAX TAT. Whole working days, MIN at least 1, MAX at least MIN. The standard must cover every pickup zone to every drop zone.',
    effect: 'It applies to every PTL merchant on the standard TAT, for shipments priced from now on.',
    downloads: [{ label: 'Download current TAT', get: () => download(KIND.TAT, null), name: 'ptl_standard_tat.csv' }],
    now: async () => {
      const g = await fetchTatGrid(null)
      return [
        { key: 'lanes', label: 'Lanes now', value: num(g.cells.length), sub: 'Pickup zone x drop zone' },
        { key: 'zones', label: 'Zones now', value: num(g.zones.length), sub: 'Defined by the standard TAT' },
        lastChange(g.latest),
      ]
    },
    checkKeys: ['lanes', 'zones', 'added', 'changed', 'unchanged', 'removed'],
  }),
  // One courier's own TAT (Merchant TAT → Courier TAT). c = a row of tat/couriers.
  'courier-tat': (c) => ({
    kind: KIND.TAT,
    merchantId: null,
    label: `${c.courier} TAT`,
    noun: `${c.courier} TAT`,
    parent: { label: c.courier, to: ROUTES.tatCourier(c.courier) },
    keyLabel: 'Lane',
    modes: false,
    fileHelp: `CSV in the Prozo Standard TAT format (Zone, Pickup Zone ID, Drop Zone ID, B2B MIN TAT, B2B MAX TAT; names are taken from the standard). Put only the lanes ${c.courier} gives a TAT for: the file is its whole TAT, a lane left out follows the Prozo standard for merchants on this courier's TAT.`,
    effect: c.merchants
      ? `It applies to the ${num(c.merchants)} PTL merchant${c.merchants === 1 ? '' : 's'} on the ${c.courier} TAT, for shipments priced from now on (their custom lanes still win).`
      : `No PTL merchant is on the ${c.courier} TAT yet, so nothing is priced from it until one is put on it in onboarding Step 2.`,
    downloads: [{
      label: c.lanes ? 'Download current TAT' : 'Download starting file (standard)',
      get: () => download(KIND.TAT, null, c.courier),
      name: `ptl_courier_tat_${c.courier}.csv`,
    }],
    now: async () => {
      const g = await fetchTatGrid(null, c.courier)
      return [
        { key: 'lanes', label: 'Courier lanes now', value: `${num(g.courierLanes)} / ${num(g.cells.length)}`, sub: 'The rest follow the standard' },
        { key: 'merchants', label: 'Merchants on it', value: num((g.usedBy || []).length), sub: 'TAT basis in Step 2' },
        lastChange(g.latest),
      ]
    },
    checkKeys: ['courierLanes', 'standardLanes', 'added', 'changed', 'unchanged', 'removed'],
    upload: ({ file, confirm, acknowledge }) => uploadCourierTat(c.courier, { file, confirm, acknowledge }),
    history: (page, size) => fetchCourierTatHistory(c.courier, page, size),
  }),
  // PTL Operations → ODA Management: ODA zone upload of one courier code (PTL_ODA_REWORK_DEV_LOG.md). c = { code, parent }.
  'oda-pincodes': (c) => ({
    kind: 'oda',
    merchantId: null,
    eyebrow: ODA_EYEBROW,
    label: `${c.code} zone map`,
    noun: `${c.code} zone map`,
    parent: { label: 'ODA Pincodes', to: `${ODA_ROUTES.pincodes}?code=${encodeURIComponent(c.code)}` },
    keyLabel: 'Pincode',
    modes: false,
    fileHelp: `The courier zone map upload of B2B Couriers with one more column: Pincode, City, State, Region, Zone, Green TAX, Special Zone, ODA Type, ODA Band. ODA Type is ODA 1, ODA 2, ODA 3, Regular or blank (blank = Regular). ODA Band is a band id of ODA Bands or blank; it sets the EDD days only when ODA Type is blank (with an ODA type, the type's days apply; with Regular it is ignored). Rows starting with # are skipped. Pincodes not in the file stay as they are. Days set by hand stay unless the row becomes Regular.`,
    effect: `It changes the ${c.code} zone map: the ODA type drives its ODA charge, and the EDD of its shipments follows ${EDD_TIMING}. The merchant price never changes.`,
    saveNote: `Saving writes the rows to the ${c.code} zone map; every ODA type, band or days change is saved in the ODA history, with the file. Any row with an error saves nothing.`,
    description: 'Check the file first: nothing is saved until you confirm. Every change of an ODA type, band or days is kept in the ODA history.',
    savedMessage: () => `${c.code} zone map saved`,
    downloads: [
      { label: 'Download sample', get: () => downloadOdaZoneSample(c.code), name: 'ptl_oda_zone_map_sample.csv' },
      { label: 'Download current zone map', get: () => downloadOdaZones(c.code), name: `ptl_oda_zone_map_${c.code}.csv` },
    ],
    now: async () => {
      const p = await fetchOdaPincodes({ code: c.code, size: 1 })
      return [
        { key: 'oda', label: 'ODA pincodes', value: num(p.odaPincodes), sub: `ODA type or km band in ${c.code}` },
        { key: 'type', label: 'With an ODA type', value: num(p.withType), sub: 'ODA type days apply' },
        { key: 'band', label: 'Band only', value: num(p.bandOnly), sub: 'Band days apply' },
        { key: 'hand', label: 'Days set by hand', value: num(p.daysByHand), sub: 'Kept by the upload' },
      ]
    },
    checkKeys: ['added', 'newOda', 'changed', 'unchanged', 'odaTypeAdded', 'odaTypeRemoved', 'bandSet', 'bandCleared', 'handDaysCleared'],
    upload: ({ file, confirm, acknowledge }) => uploadOdaZones({ code: c.code, file, confirm, acknowledge }),
  }),
}

/** Targets that need the merchant of the route. */
const MERCHANT_TARGETS = new Set(['merchant-zone-map'])
/** Targets that need the courier code of the link (?code=). */
const CODE_TARGETS = new Set(['oda-pincodes'])
/** Targets that need the courier account of the link (?courier=). */
const COURIER_TARGETS = new Set(['courier-tat'])

/**
 * The target of an Upload / History screen: right away for a standard target, after loading the PTL merchant of the
 * route for a merchant target. { t, error } with t null while loading or when the merchant is not a live PTL merchant.
 */
export function useSetupTarget(key) {
  const { merchantId } = useParams()
  const [params] = useSearchParams()
  const code = params.get('code') || ''
  const courier = params.get('courier') || ''
  const needsMerchant = MERCHANT_TARGETS.has(key)
  const needsCode = CODE_TARGETS.has(key)
  const needsCourier = COURIER_TARGETS.has(key)
  const needsLookup = needsMerchant || needsCode || needsCourier
  const [state, setState] = useState(() => (needsLookup ? { t: null, error: '' } : { t: SETUP_TARGETS[key](), error: '' }))
  useEffect(() => {
    if (!needsLookup) { setState({ t: SETUP_TARGETS[key](), error: '' }); return undefined }
    let live = true
    setState({ t: null, error: '' })
    if (needsCode) {
      fetchOdaCouriers()
        .then((list) => {
          if (!live) return
          const c = list.find((x) => x.codes.includes(code))
          setState(c ? { t: SETUP_TARGETS[key]({ code, parent: c.parent }), error: '' } : { t: null, error: `${code || 'This courier code'} is not a live PTL courier code.` })
        })
        .catch((e) => live && setState({ t: null, error: errorText(e, 'Could not load the couriers') }))
      return () => { live = false }
    }
    if (needsCourier) {
      fetchTatCouriers()
        .then((list) => {
          if (!live) return
          const c = list.find((x) => x.courier === courier)
          setState(c && !c.notLive ? { t: SETUP_TARGETS[key](c), error: '' } : { t: null, error: `${courier || 'This courier'} is not a live PTL courier account.` })
        })
        .catch((e) => live && setState({ t: null, error: errorText(e, 'Could not load the couriers') }))
      return () => { live = false }
    }
    fetchMerchants()
      .then((list) => {
        if (!live) return
        const m = list.find((x) => x.id === merchantId)
        setState(m ? { t: SETUP_TARGETS[key](m), error: '' } : { t: null, error: 'This merchant is not a live PTL merchant. Turn on PTL for it from Onboarding → Onboard a merchant first.' })
      })
      .catch((e) => live && setState({ t: null, error: errorText(e, 'Could not load the merchant') }))
    return () => { live = false }
  }, [key, merchantId, code, courier, needsLookup, needsCode, needsCourier])
  return state
}
